import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    AuctionCatalogsService,
    GetPublicAuctionSchedulers200Response,
    InvoiceDetail,
    SchedulerWithHoursData,
} from '@viewbid/ts-angular-client';
import { VbSelectOption } from '@viewbid/ui';
import { FeatureFlagUtil } from 'src/app/core/utils/feature-flag.util';
import { FeatureFlag } from 'src/app/core/constants/feature-flag.enum';

interface DeliveryOption {
    label: string;
    value: string;
    desc: string;
}

@Component({
    selector: 'vb-invoice-delivery-options',
    templateUrl: './invoice-delivery-options.component.html',
    styleUrls: ['./invoice-delivery-options.component.scss'],
})
export class InvoiceDeliveryOptionsComponent implements OnChanges {
    @Input() scheduleLater: boolean = false;
    @Input() isStepOneComplete: boolean = false;
    @Input() isPayOnlyMode: boolean = false;
    @Input() isPickupFlagged: boolean = false;
    @Input() hasPickupScheduled: boolean = false;
    /** Radio value for "Pay now, pick up later". It shares the fulfillment
     *  radio group (the choices are mutually exclusive) and is confirmed via
     *  Confirm selection like the rest — but it is a mode, not a delivery
     *  method: confirming it emits scheduleLaterToggleChanged, never
     *  deliveryMethodSelected. In pay-only mode it arrives pre-selected
     *  (the scheduleLater sync below), standing in for the retired pill
     *  toggle. */
    readonly PAY_ONLY: string = 'payOnly';
    deliveryOptions: DeliveryOption[] = [];
    @Input() selectedDeliveryMethod: string | null = null;
    @Output() deliveryMethodSelected: EventEmitter<string | null> =
        new EventEmitter<string | null>();
    @Output() stepOneComplete: EventEmitter<boolean> =
        new EventEmitter<boolean>();
    @Input() invoiceDetails: InvoiceDetail | null = null;
    @Output() scheduleLaterToggleChanged: EventEmitter<boolean> =
        new EventEmitter<boolean>();

    /** FeatureFlag.PickupFulfillment redesign — a single scheduler picker
     *  (endpoint: GET /auctions/:auctionId/schedulers) replaces the
     *  Pickup/Self-service pickup/Shipping radios. Emits the chosen
     *  scheduler so the parent can carry it forward once step 2 (the
     *  calendar/time-slot picker + booking call) is wired to book against a
     *  specific scheduler — until then, picking one is bridged onto the
     *  existing 'pickup' delivery method so booking keeps working. */
    schedulers: SchedulerWithHoursData[] = [];
    selectedSchedulerId: string | null = null;
    isLoadingSchedulers: boolean = false;
    @Output()
    selectedSchedulerChange: EventEmitter<SchedulerWithHoursData | null> =
        new EventEmitter<SchedulerWithHoursData | null>();
    /** Fires on every toggle flip, ahead of Confirm selection, so the parent
     *  can show/hide the Fulfillment details step live. Purely a display
     *  signal — scheduleLaterToggleChanged (emitted from confirmSelection)
     *  remains the one that commits the mode. */
    @Output() payOnlyPreviewChange: EventEmitter<boolean> =
        new EventEmitter<boolean>();
    private lastFetchedAuctionId: string | null = null;

    constructor(
        private auctionCatalogsService: AuctionCatalogsService,
        private featureFlagUtil: FeatureFlagUtil,
    ) {}

    get pickupFulfillmentEnabled(): boolean {
        return this.featureFlagUtil.isFeatureEnabled(
            FeatureFlag.PickupFulfillment,
        );
    }

    get schedulerOptions(): VbSelectOption<string>[] {
        return this.schedulers.map((scheduler: SchedulerWithHoursData) => ({
            value: scheduler.id ?? '',
            label: scheduler.name ?? '',
        }));
    }

    get isPayOnlySelected(): boolean {
        return this.selectedDeliveryMethod === this.PAY_ONLY;
    }

    /** Confirm is enabled once there's something to commit — a scheduler
     *  pick in the new flow, any radio in the legacy one. */
    get isConfirmDisabled(): boolean {
        if (this.pickupFulfillmentEnabled) {
            return !this.isPayOnlySelected && !this.selectedSchedulerId;
        }
        return this.selectedDeliveryMethod === null;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isStepOneComplete']) {
            this.isStepOneComplete = changes['isStepOneComplete'].currentValue;
        }
        if (changes['selectedDeliveryMethod']) {
            this.selectedDeliveryMethod =
                changes['selectedDeliveryMethod'].currentValue;
        }
        // AFTER the selectedDeliveryMethod input sync: the parent nulls its
        // selectedDeliveryMethod whenever the pay-only mode toggles, and that
        // null arrives in the same change set — the mode sync below must win
        // or the pay-only radio deselects itself right after being confirmed.
        if (changes['scheduleLater']) {
            // Keep the radio model in step with the parent-owned mode:
            // pay-only ON selects its radio; OFF clears it — unless the
            // change is the round-trip of the user picking a delivery
            // method, whose value the model already holds.
            if (this.scheduleLater) {
                this.selectedDeliveryMethod = this.PAY_ONLY;
            } else if (this.selectedDeliveryMethod === this.PAY_ONLY) {
                this.selectedDeliveryMethod = null;
                this.isStepOneComplete = false;
            }
        }
        if (changes['invoiceDetails']) {
            this.invoiceDetails = changes['invoiceDetails'].currentValue;
            this.buildOptions();
            this.loadSchedulersIfNeeded();
        }
    }

    /** GET /auctions/:auctionId/schedulers — auctionId comes off the invoice
     *  already loaded by the parent (invoiceDetails.invoice.auctionsId), so
     *  no extra invoice fetch is needed. Keyed on auctionId so repeated
     *  invoiceDetails changes (e.g. amountPaid ticking after payment) don't
     *  refetch the same list. */
    private loadSchedulersIfNeeded(): void {
        if (!this.pickupFulfillmentEnabled) return;
        const auctionId: string | undefined =
            this.invoiceDetails?.invoice?.auctionsId;
        if (!auctionId || auctionId === this.lastFetchedAuctionId) return;
        this.lastFetchedAuctionId = auctionId;
        this.isLoadingSchedulers = true;
        this.auctionCatalogsService
            .getPublicAuctionSchedulers(auctionId)
            .subscribe({
                next: (response: GetPublicAuctionSchedulers200Response) => {
                    this.schedulers = response.data ?? [];
                    this.isLoadingSchedulers = false;
                },
                error: () => {
                    this.schedulers = [];
                    this.isLoadingSchedulers = false;
                },
            });
    }

    /** Design copy per option (handoff §Step 1); Shipping joins the list only
     *  when the auction allows it. Rebuilt (not appended) so repeated
     *  invoiceDetails changes can't duplicate rows. */
    private buildOptions(): void {
        this.deliveryOptions = [
            {
                label: 'Pickup',
                value: 'pickup',
                desc: 'Collect your items in person at the warehouse.',
            },
            {
                label: 'Self-service pickup',
                value: 'proxyPickup',
                desc: 'Someone else collects on your behalf.',
            },
        ];
        if (this.invoiceDetails?.auction?.allowShipping) {
            this.deliveryOptions.push({
                label: 'Shipping',
                value: 'shipping',
                desc: "We'll quote shipping to your address.",
            });
        }
    }

    /** "Pay now, pick up later" joins the radio list whenever a balance
     *  remains — flagged buyers must schedule, so they never see it. */
    get showPayLaterOption(): boolean {
        return (
            !!this.invoiceDetails &&
            !this.invoiceDetails.invoice?.isPaidInFull &&
            !this.isPickupFlagged
        );
    }

    selectOption(value: string): void {
        this.selectedDeliveryMethod = value;
    }

    onSchedulerChange(schedulerId: string): void {
        this.selectedSchedulerId = schedulerId;
    }

    /** The pay-only toggle shares the legacy PAY_ONLY model so the existing
     *  scheduleLater sync (ngOnChanges above) keeps working unchanged. Also
     *  fires the live preview immediately — see payOnlyPreviewChange. */
    onPayOnlyToggle(checked: boolean): void {
        this.selectedDeliveryMethod = checked ? this.PAY_ONLY : null;
        this.payOnlyPreviewChange.emit(checked);
    }

    /** Confirms whatever is selected. A delivery method (legacy radios) or a
     *  scheduler (new flow) emits deliveryMethodSelected; "Pay only, pick up
     *  later" instead enters (or leaving it exits) the pay-only mode via
     *  scheduleLaterToggleChanged. Selecting alone commits nothing — every
     *  choice, pay-only included, takes effect here. Always visible
     *  (disabled while nothing is selected); re-confirming a committed
     *  choice just re-advances the stepper. */
    confirmSelection(): void {
        if (this.pickupFulfillmentEnabled) {
            this.confirmSchedulerSelection();
            return;
        }
        if (!this.selectedDeliveryMethod) return;
        const payOnly: boolean = this.selectedDeliveryMethod === this.PAY_ONLY;
        if (payOnly !== this.scheduleLater) {
            this.scheduleLater = payOnly;
            this.scheduleLaterToggleChanged.emit(payOnly);
        }
        this.isStepOneComplete = true;
        this.stepOneComplete.emit(true);
        if (!payOnly) {
            this.deliveryMethodSelected.emit(this.selectedDeliveryMethod);
        }
    }

    private confirmSchedulerSelection(): void {
        if (this.isConfirmDisabled) return;
        const payOnly: boolean = this.isPayOnlySelected;
        if (payOnly !== this.scheduleLater) {
            this.scheduleLater = payOnly;
            this.scheduleLaterToggleChanged.emit(payOnly);
        }
        this.isStepOneComplete = true;
        this.stepOneComplete.emit(true);
        if (!payOnly) {
            // Bridge onto the legacy 'pickup' method so step 2's calendar/
            // time-slot picker and the createPickup() call keep working
            // until they're updated to book against a specific scheduler.
            this.selectedDeliveryMethod = 'pickup';
            this.deliveryMethodSelected.emit(this.selectedDeliveryMethod);
            const scheduler: SchedulerWithHoursData | null =
                this.schedulers.find(
                    (s: SchedulerWithHoursData) =>
                        s.id === this.selectedSchedulerId,
                ) ?? null;
            this.selectedSchedulerChange.emit(scheduler);
        }
    }
}
